import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { CallbackDomainField } from "@/components/admin/CallbackDomainField";

export const emptyDefaults = (d = {}) => ({
    customer: { first_name: "", last_name: "", email: "", mobile: "", ...(d.customer || {}) },
    notification_url: d.notification_url || "",
    return_url: d.return_url || "",
    flow: d.flow || "",
});

function CopyIcon({ text, testid }) {
    const [ok, setOk] = useState(false);
    return (
        <button type="button" data-testid={testid} title="Copy"
                onClick={async () => { try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); } catch { /* blocked */ } }}
                className="p-1 text-slate-400 hover:text-slate-800">
            {ok ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
    );
}

const In = ({ value, onChange, placeholder, testid, mono }) => (
    <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
           className={`input w-full text-sm py-1.5 ${mono ? "font-mono" : ""}`} />
);

/** Admin-only per-partner PayIn defaults: walk-in customer + notification/return URLs. */
export function PartnerDefaults({ value, onChange, defaultHook, partnerId, callbackDomain, onHookChange }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    const setC = (k, v) => onChange({ ...value, customer: { ...value.customer, [k]: v } });
    const effectiveHook = value.notification_url || defaultHook;
    const usingPartnerUrl = value.notification_url && value.notification_url === defaultHook;
    return (
        <div className="border border-slate-200 rounded-lg p-3 space-y-3" data-testid="payin-defaults-field">
            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">PayIn defaults for this partner</label>
            {partnerId && (
                <CallbackDomainField key={partnerId} value={callbackDomain} testid="payin-partner-domain"
                                     label="Callback domain (neutral host the vendor sees)"
                                     hint="Optional. Leave empty to use the neutral fallback domain (Partner wallets tab)."
                                     setPath={`/admin/payin/wallet/${partnerId}/callback-domain`}
                                     verifyPath={`/admin/payin/wallet/${partnerId}/callback-domain/verify`}
                                     onUpdated={(d) => d.default_notification_url && onHookChange?.(d.default_notification_url)} />
            )}
            <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Notification URL (give this to the vendor for this partner's MID)</div>
                <In value={value.notification_url} onChange={(v) => set("notification_url", v)} placeholder={defaultHook} testid="payin-defaults-hook" mono />
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                    {value.notification_url ? (
                        <span className="truncate">Effective: <span className="font-mono text-slate-700" data-testid="payin-defaults-hook-effective">{effectiveHook}</span></span>
                    ) : (
                        <span className="truncate" data-testid="payin-defaults-hook-effective">Empty → uses the MID's notification URL, or the partner URL if the MID has none</span>
                    )}
                    <CopyIcon text={effectiveHook} testid="payin-defaults-hook-copy" />
                    {!usingPartnerUrl && (
                        <button type="button" onClick={() => set("notification_url", defaultHook)} data-testid="payin-defaults-hook-use"
                                className="ml-auto shrink-0 font-semibold text-indigo-600 hover:text-indigo-800">Use partner URL</button>
                    )}
                </div>
            </div>
            <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">PayIn flow for this partner</div>
                <select value={value.flow || ""} onChange={(e) => set("flow", e.target.value)} data-testid="payin-defaults-flow"
                        className="input w-full text-sm py-1.5">
                    <option value="">Use the MID's default flow</option>
                    <option value="direct">Direct (HPP / QR / UPI)</option>
                    <option value="redirect">Redirect (Payspher cashier page)</option>
                </select>
            </div>
            <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Return URL (customer lands here after paying)</div>
                <In value={value.return_url} onChange={(v) => set("return_url", v)} placeholder="https://partner-site.com/thank-you" testid="payin-defaults-return" mono />
            </div>
            <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Default customer (used when order has no customer details)</div>
                <div className="grid grid-cols-2 gap-2">
                    <In value={value.customer.first_name} onChange={(v) => setC("first_name", v)} placeholder="First name · Customer" testid="payin-defaults-first" />
                    <In value={value.customer.last_name} onChange={(v) => setC("last_name", v)} placeholder="Last name · Walk-in" testid="payin-defaults-last" />
                    <In value={value.customer.email} onChange={(v) => setC("email", v)} placeholder="walkin@partner.com" testid="payin-defaults-email" />
                    <In value={value.customer.mobile} onChange={(v) => setC("mobile", v)} placeholder="0000000000" testid="payin-defaults-mobile" />
                </div>
            </div>
        </div>
    );
}
